"use client";

import { InsoleSide, Role } from "@prisma/client";
import { useState } from "react";
import Button, { ButtonSize, ButtonVariant } from "@/app/(components)/generics/Button/Button";
import FileInput from "@/app/(components)/generics/inputs/default/FileInput/FileInput";
import { Input } from "@/app/(components)/generics/inputs/default/Input/Input";
import { SelectInput } from "@/app/(components)/generics/inputs/default/SelectInput/SelectInput";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { deleteInsoleModel, updateInsoleModel } from "@/app/actions/insole-model/action";
import {
  isValidInsoleFile,
  isValidPreviewImage,
  prepareInsoleUpload,
  preparePreviewUpload,
} from "@/app/libs/InsoleUpload";
import { ConfirmStore } from "@/app/stores/ConfirmStore";
import { UserStore } from "@/app/stores/UserStore";
import { ViewStore } from "@/app/stores/ViewStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import { PublicInsoleModel } from "@/app/types/PublicInsoleModel";
import { detectInsoleSideFromFile } from "@/app/utils/UInsoleSide";
import { Globals } from "@/app/vars/Globals";
import styles from "./InsoleDetail.module.css";

type Props = {
  model: PublicInsoleModel;
  lang: string;
  onSuccess: () => void;
  onDelete?: () => void;
};

export default function InsoleDetail(props: Props) {
  const { model, lang, onSuccess, onDelete } = props;

  const user = UserStore((state) => state.user);
  const isAdmin = user?.role === Role.ADMIN;
  const isAdminView = ViewStore((state) => state.isAdminView);
  const canManageVisibility = isAdmin && isAdminView;

  const [data, setData] = useState({
    name: model.name,
    visibility: model.isPublic ? "public" : "personal",
  });
  const [file, setFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    if (file && !isValidInsoleFile(file)) {
      setFieldErrors(TErrors({ file: TKey.ERROR_INVALID_FILE }, lang));
      return;
    }

    if (previewFile && !isValidPreviewImage(previewFile)) {
      setFieldErrors(TErrors({ previewFile: TKey.ERROR_INVALID_FILE }, lang));
      return;
    }

    setLoading(true);

    const isPublic = canManageVisibility ? data.visibility === "public" : model.isPublic;

    try {
      const side = file ? await detectInsoleSideFromFile(file) : (model.side as InsoleSide);
      const prepared = file ? await prepareInsoleUpload(file, isPublic) : {};
      const preparedPreview = previewFile ? await preparePreviewUpload(previewFile, isPublic) : {};
      const result = await updateInsoleModel(
        model.uuid,
        {
          name: data.name,
          side,
          isPublic,
          file: prepared.file,
          fileUrl: prepared.fileUrl,
          previewFile: preparedPreview.file,
          previewUrl: preparedPreview.fileUrl,
        },
        lang,
      );

      if (result.success) {
        onSuccess();
      } else if (result.fieldErrors) {
        setFieldErrors(TErrors(result.fieldErrors, lang));
      } else {
        setError(T(TKey.ERROR, lang) as string);
      }
    } catch {
      setError(T(TKey.ERROR, lang) as string);
    }

    setLoading(false);
  };

  const handleDelete = () => {
    ConfirmStore.getState().open({
      title: T(TKey.CONFIRM_TITLE, lang) as string,
      message: T(TKey.CONFIRM_DELETE_INSOLE_MODEL, lang) as string,
      confirmLabel: T(TKey.DELETE_INSOLE_MODEL, lang) as string,
      onConfirm: async () => {
        const result = await deleteInsoleModel(model.uuid);
        if (!result.success) {
          return { error: T((result.error as TKey) ?? TKey.ERROR, lang) as string };
        }
        (onDelete ?? onSuccess)();
      },
    });
  };

  if (!isAdmin) {
    return (
      <div className={styles.detail}>
        <p className={styles.name}>{model.name}</p>
      </div>
    );
  }

  return (
    <form className={styles.detail} onSubmit={handleSubmit}>
      <Input
        data={data}
        dataKey="name"
        label={T(TKey.INSOLE_MODEL_NAME, lang) as string}
        lang={lang}
        required
        showRequiredLabel
        placeholder={T(TKey.INSOLE_MODEL_NAME, lang) as string}
        onChange={(d) => setData({ ...d })}
        errorMessage={fieldErrors.name}
      />

      {canManageVisibility && (
        <SelectInput
          data={data}
          dataKey="visibility"
          label={T(TKey.INSOLE_MODEL_VISIBILITY, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          options={[
            ["public", T(TKey.INSOLE_MODEL_PUBLIC, lang) as string],
            ["personal", T(TKey.INSOLE_MODEL_PERSONAL, lang) as string],
          ]}
          onChange={(d) => setData({ ...d })}
        />
      )}

      <FileInput
        lang={lang}
        label={T(TKey.INSOLE_MODEL_FILE, lang) as string}
        accept={Globals.INSOLE_MODEL_FILE_EXTENSION}
        hint={T(TKey.INSOLE_MODEL_FILE_HINT, lang) as string}
        selectLabel={T(TKey.INSOLE_MODEL_FILE_REPLACE, lang) as string}
        value={file}
        onChange={setFile}
        errorMessage={fieldErrors.file}
      />

      <FileInput
        lang={lang}
        label={T(TKey.INSOLE_MODEL_PREVIEW, lang) as string}
        accept="image/*"
        hint={T(TKey.INSOLE_MODEL_PREVIEW_HINT, lang) as string}
        selectLabel={T(TKey.INSOLE_MODEL_PREVIEW_SELECT, lang) as string}
        value={previewFile}
        onChange={setPreviewFile}
        errorMessage={fieldErrors.previewFile}
      />

      {error && <ErrorMessage message={error} />}

      <div className={styles.actions}>
        <Button
          variant={ButtonVariant.DANGER}
          size={ButtonSize.SM}
          type="button"
          onClick={handleDelete}
        >
          {T(TKey.DELETE_INSOLE_MODEL, lang)}
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SM}
          type="submit"
          loading={loading}
        >
          {T(TKey.SAVE, lang)}
        </Button>
      </div>
    </form>
  );
}
